import { Checkbox } from '@/components/ui/checkbox';
import { Box, Flex, Text, VStack } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { BoostAuthorizationHandshake, type BoostIdentity } from './BoostAuthorizationHandshake';
import { BoostFooter } from './BoostFooter';
import type { BoostFooterIntent } from './boost-footer-intent';

interface MentionBoostRequestPanelProps {
  // Whether the inline scope checkbox is offered. When false the request is sent
  // silently at account level and no checkbox renders.
  contentOnlyOptionAvailable: boolean;
  // The brand + creator identity framing the ask. Optional: without it the
  // panel still works, just without the handshake hero.
  identity?: BoostIdentity;
  // Bound to the scope checkbox: on = content-level (this mention only).
  limitToMention: boolean;
  onLimitToMentionChange: (checked: boolean) => void;
  onConfirm: () => void;
  submitting: boolean;
}

/**
 * Inline authorization-request surface. Replaces the former centered preflight
 * dialog: it lives in the same right pane as the decision and setup views, so
 * asking the creator for permission reads as a step, not a takeover. The scroll
 * body leads with the brand-to-creator handshake (shield chip: the ask ahead),
 * then the centered explainer and the optional scope checkbox; the sticky
 * footer carries the single "request authorization" action.
 */
export function MentionBoostRequestPanel({
  contentOnlyOptionAvailable,
  identity,
  limitToMention,
  onLimitToMentionChange,
  onConfirm,
  submitting,
}: MentionBoostRequestPanelProps) {
  const { t } = useTranslation();

  const footerIntent: BoostFooterIntent = {
    mode: 'action',
    labelKey: 'mentions.boost.confirm.requestCta',
    onClick: onConfirm,
  };

  return (
    <Flex flex="1" minH={0} direction="column" position="relative">
      <Box flex="1" overflowY="auto" px={{ base: 4, md: 6 }} pt={5} pb={{ base: 28, md: 32 }}>
        <VStack align="stretch" gap={4}>
          {identity && (
            <Box pt={2} pb={1}>
              <BoostAuthorizationHandshake {...identity} icon="shield" />
            </Box>
          )}

          <Text fontSize="sm" color="gray.700" lineHeight="tall" textAlign="center">
            {t('mentions.boost.confirm.description')}
          </Text>

          {contentOnlyOptionAvailable && (
            <Checkbox
              checked={limitToMention}
              onCheckedChange={(details) => onLimitToMentionChange(details.checked === true)}
              size="sm"
              alignItems="flex-start"
              gap={2.5}
              border="1px solid"
              borderColor="gray.200"
              rounded="lg"
              px={3}
              py={2.5}
              w="full"
            >
              <Text fontSize="sm" color="gray.700">
                {t('mentions.boost.confirm.limitToMention')}
              </Text>
            </Checkbox>
          )}
        </VStack>
      </Box>

      <BoostFooter intent={submitting ? { mode: 'skeleton' } : footerIntent} />
    </Flex>
  );
}
